import React, { useState } from 'react';
import axios from 'axios';
import { Box, Typography, Button, Alert, CircularProgress, Paper, TextField, InputAdornment, IconButton } from '@mui/material';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { useAuth } from '../../contexts/AuthContext';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../../config/firebase';

// API URL for the Java backend - without the /api suffix since it's included in the endpoints
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080';

const JsonView = ({ src }: { src: any }) => {
  return (
    <Box
      sx={{
        p: 2,
        backgroundColor: '#272822',
        color: '#f8f8f2',
        borderRadius: 1,
        overflow: 'auto',
        maxHeight: '400px',
        fontFamily: 'monospace'
      }}
    >
      <pre>{JSON.stringify(src, null, 2)}</pre>
    </Box>
  );
};

const AdminRobinhoodPage: React.FC = () => {
  const { state } = useAuth();
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [rawData, setRawData] = useState<any>(null);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [actualPassword, setActualPassword] = useState('');
  const [healthStatus, setHealthStatus] = useState<any>(null);
  const [fetchingCreds, setFetchingCreds] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  // Fetch credentials directly from Firebase
  const fetchCredentialsFromFirebase = async () => {
    setFetchingCreds(true);
    setError(null);
    try {
      if (!state.user || !state.user.id) {
        setError('You must be logged in to fetch Robinhood credentials');
        setFetchingCreds(false);
        return;
      }
      
      console.log('Fetching Robinhood credentials directly from Firebase...');
      console.log('User ID:', state.user.id);
      
      const apiCredentialsRef = collection(db, `users/${state.user.id}/api_credentials`);
      const robinhoodQuery = query(apiCredentialsRef, where("provider", "==", "robinhood"));
      const querySnapshot = await getDocs(robinhoodQuery);
      
      if (!querySnapshot.empty) {
        const credentialDoc = querySnapshot.docs[0];
        const credentialData = credentialDoc.data();
        console.log('Firebase credentials raw data:', credentialData);
        
        if (credentialData) {
          setUsername(credentialData.username || '');
          if (credentialData.password) {
            setPassword('********');
            setActualPassword(credentialData.password);
            console.log('Direct Firebase password:', credentialData.password);
          } else {
            setPassword('');
            setActualPassword('');
          }
        }
      } else {
        console.log('No Robinhood credentials found in Firebase');
        setError('No Robinhood credentials found');
      }
    } catch (err: any) {
      console.error('Error fetching Robinhood credentials from Firebase:', err);
      setError('Failed to fetch Robinhood credentials from Firebase: ' + (err.message || ''));
    } finally {
      setFetchingCreds(false);
    }
  };
  
  // Fetch credentials for the current user through backend
  const fetchRobinhoodCredentials = async () => {
    setFetchingCreds(true);
    setError(null);
    try {
      if (!state.user || !state.user.email) {
        setError('You must be logged in to fetch Robinhood credentials');
        setFetchingCreds(false);
        return;
      }
      
      console.log('Fetching Robinhood credentials...');
      console.log('User ID:', state.user.email);
      
      // The backend expects only userId parameter, no email
      const resp = await axios.get(`${API_URL}/api/admin/robinhood/api-keys`, {
        params: {
          userId: state.user.email
        },
        timeout: 10000
      });
      
      console.log('Credentials response:', resp.data);
      // Log the exact password value to ensure it's coming through correctly
      console.log('Actual password from API:', resp.data.password);
      
      setUsername(resp.data.username);
      if (resp.data.password) {
        setPassword('********');
        const actualPwd = resp.data.password;
        console.log('Setting actual password to:', actualPwd);
        setActualPassword(actualPwd); // Store the actual password
      } else {
        setPassword('');
        setActualPassword('');
        console.log('No password found in response');
      }
    } catch (err: any) {
      console.error('Error fetching Robinhood credentials:', err);
      setError(err.response?.data?.message || 'Failed to fetch Robinhood credentials for user');
    } finally {
      setFetchingCreds(false);
    }
  };

  // Health check for Robinhood integration
  const checkRobinhoodApiHealth = async () => {
    setLoading(true);
    setError(null);
    setHealthStatus(null);
    try {
      if (!state.user || !state.user.email) {
        setError('You must be logged in to check Robinhood API health');
        setLoading(false);
        return;
      }
      
      console.log('Checking Robinhood API health...');
      
      // The health endpoint doesn't require any parameters
      const response = await axios.get(`${API_URL}/api/admin/robinhood/health`, {
        timeout: 10000
      });
      
      console.log('Health check response:', response.data);
      setHealthStatus(response.data);
    } catch (error: any) {
      console.error('Error checking Robinhood API health:', error);
      
      let errorMessage = 'Failed to check Robinhood API health';
      
      if (error.response) {
        console.error('Error response:', error.response);
        if (error.response.data && error.response.data.message) {
          errorMessage = `${errorMessage}: ${error.response.data.message}`;
        }
      } else if (error.request) {
        errorMessage = `${errorMessage}: No response received (timeout or network issue)`;
      } else if (error.message) {
        errorMessage = `${errorMessage}: ${error.message}`;
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Fetch raw Robinhood data for the current user
  const fetchRawRobinhoodData = async () => {
    setLoading(true);
    setError(null);
    setRawData(null);
    // Log password state at the beginning of the function
    console.log('Current actualPassword state:', actualPassword);
    try {
      if (!state.user || !state.user.email) {
        setError('You must be logged in to fetch Robinhood raw data');
        setLoading(false);
        return;
      }
      
      // First, get the credentials for the user
      console.log('First getting Robinhood credentials for raw data fetch...');
      console.log('User email:', state.user.email);
      
      const credsResponse = await axios.get(`${API_URL}/api/admin/robinhood/api-keys`, {
        params: {
          userId: state.user.email
        },
        timeout: 10000
      });
      
      const username = credsResponse.data.username;
      const password = credsResponse.data.password;
      
      if (!username || !password) {
        setError('Robinhood credentials not found or incomplete');
        setLoading(false);
        return;
      }
      
      console.log('Fetching completely unmodified raw Robinhood data...');
      
      // The raw-data endpoint is a POST method that expects username and password
      const response = await axios.post(`${API_URL}/api/admin/robinhood/raw-data`, {
        username,
        password
      }, {
        headers: {
          'Content-Type': 'application/json'
        },
        timeout: 60000 // 60 second timeout for potentially large data
      });
      
      console.log('Raw Robinhood data response:', response.data);
      setRawData(response.data);
    } catch (err: any) {
      console.error('Error fetching raw Robinhood data:', err);
      
      let errorMessage = 'Failed to fetch Robinhood raw data';
      
      if (err.response) {
        console.error('Error response:', err.response);
        if (err.response.data && err.response.data.message) {
          errorMessage = `${errorMessage}: ${err.response.data.message}`;
        }
      } else if (err.request) {
        errorMessage = `${errorMessage}: No response received (timeout or network issue)`;
      } else if (err.message) {
        errorMessage = `${errorMessage}: ${err.message}`;
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Paper sx={{ p: 4, mt: 4 }}>
      <Typography variant="h5" gutterBottom>
        Robinhood Admin Dashboard
      </Typography>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
        <Button 
          variant="contained" 
          color="primary" 
          onClick={fetchRawRobinhoodData} 
          disabled={loading}
        >
          Get Raw Robinhood Data
        </Button>
        <Button 
          variant="contained" 
          color="secondary" 
          onClick={checkRobinhoodApiHealth} 
          disabled={loading}
        >
          Check API Health
        </Button>
        <Button 
          variant="contained" 
          color="info" 
          onClick={fetchRobinhoodCredentials} 
          disabled={fetchingCreds}
        >
          See Credentials (Backend)
        </Button>
        <Button 
          variant="contained" 
          color="warning" 
          onClick={fetchCredentialsFromFirebase} 
          disabled={fetchingCreds}
        >
          See Credentials (Direct)
        </Button>
      </Box>
      <Alert severity="info" sx={{ mb: 2 }}>
        This page displays completely unmodified raw data from the Robinhood API for debugging purposes.
      </Alert>
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
          <CircularProgress />
        </Box>
      )}
      {rawData && (
        <Paper elevation={3} sx={{ p: 2, mb: 3 }}>
          <Typography variant="h6" gutterBottom>Raw Robinhood Account Data</Typography>
          <JsonView src={rawData} />
        </Paper>
      )}
      {healthStatus && (
        <Paper elevation={3} sx={{ p: 2, mb: 3 }}>
          <Typography variant="h6" gutterBottom>Robinhood API Health Status</Typography>
          <JsonView src={healthStatus} />
        </Paper>
      )}
      {(username || password) && (
        <Paper elevation={3} sx={{ p: 2, mb: 3 }}>
          <Typography variant="h6" gutterBottom>Robinhood Credentials</Typography>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              label="Robinhood Username"
              value={username}
              InputProps={{ readOnly: true }}
              size="small"
              variant="outlined"
            />
            <Box sx={{ position: 'relative' }}>
              {/* Use two separate text fields for better control */}
              {showPassword ? (
                <TextField
                  label="Robinhood Password"
                  value={actualPassword}
                  InputProps={{
                    readOnly: true,
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="toggle password visibility"
                          onClick={() => setShowPassword(false)}
                          edge="end"
                          size="small"
                        >
                          <VisibilityOff />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  size="small"
                  variant="outlined"
                />
              ) : (
                <TextField
                  label="Robinhood Password"
                  value={password}
                  InputProps={{
                    readOnly: true,
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="toggle password visibility"
                          onClick={() => setShowPassword(true)}
                          edge="end"
                          size="small"
                        >
                          <Visibility />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  size="small"
                  variant="outlined"
                  type="password"
                />
              )}
            </Box>
          </Box>
          {/* Debug info */}
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            Password value in state: {actualPassword ? actualPassword.length + ' characters' : 'none'}
          </Typography>
        </Paper>
      )}
      {!(username || password) && (
        <Alert severity="info" sx={{ mb: 2 }}>
          No Robinhood credentials found for this user. Please set up credentials in the <a href="/broker-config">Broker Configuration</a> page.
        </Alert>
      )}
    </Paper>
  );
};

export default AdminRobinhoodPage;
